/**
 * RecordsTable - Table display for records with expandable rows
 *
 * Atomic level: Organism
 * Composes: Table (shadcn), ConductScoreIndicator
 *
 * Features:
 * - Clickable rows that expand to show RecordDetail
 * - Conduct Score badge with color coding (1-10 scale)
 * - Summary preview with truncation
 */

import React from 'react';
import { cn } from '../../../../lib/utils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../ui/table';
import { Checkbox } from '../../../ui/checkbox';
import { Timestamp } from '../../../ui/timestamp';
import IconWrapper from '../../../ui/IconWrapper';
import { FiChevronDown, FiChevronRight } from 'react-icons/fi';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { ConductScoreIndicator } from './ConductScoreIndicator';
import { RecordDetail } from './RecordDetail';
import type { Record } from '../../../../types/record';
import { isRecordProcessing } from '../../../../utils/processingStatus';

type SortableColumn = 'conduct_score' | 'received_at' | 'customer_name';

interface RecordsTableProps {
  records: Record[];
  expandedRecordId: string | null;
  onRecordClick: (recordId: string) => void;
  vulnerabilityDetectionEnabled?: boolean;
  onReclassify?: (recordId: string) => void;
  reclassifyingRecordId?: string | null;
  className?: string;
  sortBy?: SortableColumn;
  sortOrder?: 'asc' | 'desc';
  onSort?: (column: SortableColumn) => void;
}

const RecordsTable: React.FC<RecordsTableProps> = ({
  records,
  expandedRecordId,
  onRecordClick,
  vulnerabilityDetectionEnabled = true,
  onReclassify,
  reclassifyingRecordId,
  className,
  sortBy = 'conduct_score',
  sortOrder = 'desc',
  onSort,
}) => {
  const renderSortIndicator = (column: SortableColumn) => {
    if (sortBy !== column) {
      return null;
    }
    const Icon = sortOrder === 'desc' ? ArrowDown : ArrowUp;
    return <Icon className="w-3 h-3 text-muted-foreground" aria-hidden="true" />;
  };

  const handleHeaderKeyDown = (column: SortableColumn) => (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSort?.(column);
    }
  };

  const sortableHeadClasses =
    'font-semibold text-xs uppercase tracking-wide text-muted-foreground cursor-pointer select-none hover:bg-muted/50';

  const resolveAriaSort = (column: SortableColumn): 'ascending' | 'descending' | 'none' => {
    if (sortBy !== column) return 'none';
    return sortOrder === 'desc' ? 'descending' : 'ascending';
  };

  return (
    <div className={cn('overflow-x-auto', className)}>
      <Table className="min-w-[1180px] w-full table-auto">
        <TableHeader>
          <TableRow className="border-b border-border/70 bg-muted/45 hover:bg-muted/45">
            <TableHead className="w-12">
              <Checkbox
                aria-label="Select all records"
                className="border-border"
              />
            </TableHead>
            <TableHead className="w-10"></TableHead>
            <TableHead
              className={cn('w-32', sortableHeadClasses)}
              onClick={() => onSort?.('conduct_score')}
              aria-sort={resolveAriaSort('conduct_score')}
              tabIndex={0}
              onKeyDown={handleHeaderKeyDown('conduct_score')}
            >
              <div className="flex items-center gap-1">
                Conduct Score
                {renderSortIndicator('conduct_score')}
              </div>
            </TableHead>
            <TableHead
              className={cn('w-36', sortableHeadClasses)}
              onClick={() => onSort?.('received_at')}
              aria-sort={resolveAriaSort('received_at')}
              tabIndex={0}
              onKeyDown={handleHeaderKeyDown('received_at')}
            >
              <div className="flex items-center gap-1">
                Date
                {renderSortIndicator('received_at')}
              </div>
            </TableHead>
            <TableHead
              className={cn('w-56', sortableHeadClasses)}
              onClick={() => onSort?.('customer_name')}
              aria-sort={resolveAriaSort('customer_name')}
              tabIndex={0}
              onKeyDown={handleHeaderKeyDown('customer_name')}
            >
              <div className="flex items-center gap-1">
                Customer Name
                {renderSortIndicator('customer_name')}
              </div>
            </TableHead>
            <TableHead className="w-[38%] font-semibold text-xs uppercase tracking-wide text-muted-foreground">
              Summary
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {records.map((record) => {
            const isExpanded = expandedRecordId === record.id;

            return (
              <React.Fragment key={record.id}>
                <TableRow
                  className={cn(
                    'cursor-pointer border-b transition-colors hover:bg-muted/20',
                    isExpanded && 'bg-muted/15'
                  )}
                  onClick={() => onRecordClick(record.id)}
                >
                  <TableCell className="py-4" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      aria-label={`Select record ${record.id}`}
                      className="border-border"
                    />
                  </TableCell>
                  <TableCell className="py-4">
                    <IconWrapper
                      icon={isExpanded ? FiChevronDown : FiChevronRight}
                      className="size-5 text-muted-foreground"
                    />
                  </TableCell>
                  <TableCell className="py-4">
                    <ConductScoreIndicator
                      score={record.conductScore}
                      size="small"
                      isProcessing={isRecordProcessing(record)}
                      processingStage={record._progress?.stage || record.processing_status}
                    />
                  </TableCell>
                  <TableCell className="w-36 py-4 text-sm text-muted-foreground">
                    <Timestamp value={record.date} format="date-only" />
                  </TableCell>
                  <TableCell className="w-56 py-4">
                    <span className="block truncate text-sm text-foreground">{record.customerName}</span>
                  </TableCell>
                  <TableCell className="w-[38%] py-4">
                    <p className="line-clamp-2 max-w-full text-sm leading-6 text-foreground">
                      {record.summary}
                    </p>
                  </TableCell>
                </TableRow>

                {isExpanded && (
                  <TableRow className="hover:bg-transparent">
                    <TableCell
                      colSpan={7}
                      className="border-b bg-card p-0"
                      style={{ width: '100%', maxWidth: '100%' }}
                    >
                      <RecordDetail
                        record={record}
                        vulnerabilityDetectionEnabled={vulnerabilityDetectionEnabled}
                        onReclassify={onReclassify}
                        isReclassifying={reclassifyingRecordId === record.id}
                      />
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

export { RecordsTable };
